import type { MeasurementsSummary } from '../types';
import { readableKey } from '../settings/resolve';
import type { OutputRendererProps } from './types';

const NONE = '—';

/** Trailing zeros off: 15.0 → "15". */
function trim(v: number, digits: number): string {
  return String(Number(v.toFixed(digits)));
}

/**
 * A measured value in words. 'ratio' is a share, shown as a percentage; '1' is
 * a plain number; any other unit follows the number. Below 0 or missing is
 * "—": HistoQC writes -1 for a measurement it could not make.
 */
export function formatMeasurement(value: unknown, unit: string | null | undefined): string {
  const n = typeof value === 'number' ? value : typeof value === 'string' && value.trim() !== '' ? Number(value) : NaN;
  if (!Number.isFinite(n) || n < 0) return NONE;
  if (unit === 'ratio') return `${trim(n * 100, n * 100 < 10 ? 1 : 0)}%`;
  const shown = Math.abs(n) >= 100 ? trim(n, 0) : Math.abs(n) >= 1 ? trim(n, 2) : trim(n, 3);
  return unit && unit !== '1' ? `${shown} ${unit}` : shown;
}

export interface MeasurementRow {
  key: string;
  title: string;
  value: string;
}

/** The summary's fields as table rows, titled and united by the summary or else by the action's declaration. */
export function measurementRows(
  summary: unknown,
  declared?: Record<string, { title: string; unit?: string }>,
): MeasurementRow[] {
  const fields = (summary as MeasurementsSummary | null | undefined)?.fields ?? {};
  const keys = [...Object.keys(declared ?? {}).filter((k) => k in fields), ...Object.keys(fields).filter((k) => !(declared && k in declared))];
  return keys.map((key) => {
    const f = fields[key];
    const d = declared?.[key];
    return {
      key,
      title: f?.title || d?.title || readableKey(key),
      value: formatMeasurement(f?.value, f?.unit ?? d?.unit),
    };
  });
}

/** Measurements: a table of fields — title, value. */
export function MeasurementsOutputs({ outputs, declared }: OutputRendererProps) {
  return (
    <>
      {outputs.map((o) => {
        const rows = measurementRows(o.summary, declared?.[o.key]?.fields);
        if (rows.length === 0) return null;
        return (
          <div key={o.key} className="flex flex-col gap-1">
            {o.title ? <div className="text-xs font-medium text-muted-foreground">{o.title}</div> : null}
            <table className="w-full text-sm">
              <tbody>
                {rows.map((r) => (
                  <tr key={r.key} className="border-b border-border/60 last:border-0">
                    <th scope="row" className="py-1 pr-2 text-left font-normal text-muted-foreground">
                      {r.title}
                    </th>
                    <td className="py-1 text-right tabular-nums text-foreground">{r.value}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        );
      })}
    </>
  );
}
